import { zodResolver } from '@hookform/resolvers/zod'
import { AddOutlined, DeleteOutlined } from '@mui/icons-material'
import { Box, Card, Divider, IconButton, Stack, Typography } from '@mui/material'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DateTime } from 'luxon'
import { useState } from 'react'
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import CustomButton from '@/components/inputs/CustomButton'
import CustomSelectField from '@/components/inputs/CustomSelectField'
import TextFieldController from '@/components/inputs/TextFieldController'
import CustomCardActions from '@/components/layouts/CustomCardActions'
import CustomCardContent from '@/components/layouts/CustomCardContent'
import ErrorMessage from '@/features/resource/components/ErrorMessage'
import { PaginationControls } from '@/features/resource/components/rampCurves/PaginationControls'
import { getRampCurveSchema } from '@/features/resource/schemas'
import type {
  AddRampCurveRequest,
  RampCurve,
  RampCurveSchemaType,
  UpdateRampCurveRequest,
} from '@/features/resource/types'
import { convertToKilo } from '@/features/resource/utils/convertToKilo'
import { convertToWatts } from '@/features/resource/utils/convertToWatts'

type RampCurveFormProps = {
  initialData?: RampCurve
  onSave: (data: AddRampCurveRequest | UpdateRampCurveRequest) => void
  onCancel: () => void
  onDelete?: () => void
}

const RampCurveForm = ({ initialData, onSave, onCancel, onDelete }: RampCurveFormProps) => {
  const { t } = useTranslation()
  const ENTRIES_PER_PAGE = 10

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1)
  const [pageNumberInput, setPageNumberInput] = useState('')

  const methods = useForm<RampCurveSchemaType>({
    defaultValues: initialData
      ? {
          name: initialData.name,
          meteringPortId: initialData.meteringPortId,
          verifiedAt: initialData.verifiedAt,
          direction: initialData.direction,
          rampCurveEntries: initialData.rampCurveEntries.map((entry) => ({
            durationSinceStartMillis: entry.durationSinceStartMillis,
            consumption: convertToKilo(entry.consumption),
          })),
        }
      : {
          name: '',
          meteringPortId: null,
          verifiedAt: null,
          direction: 'UP',
          rampCurveEntries: [],
        },
    resolver: zodResolver(getRampCurveSchema(t)),
    mode: 'onSubmit',
  })

  const {
    handleSubmit,
    formState: { errors },
    control,
  } = methods

  const { fields, append, remove, insert } = useFieldArray({
    control,
    name: 'rampCurveEntries',
  })

  // Pagination calculations
  const totalPages = Math.ceil(fields.length / ENTRIES_PER_PAGE)

  const startIndex = (currentPage - 1) * ENTRIES_PER_PAGE
  const currentPageFields = fields.slice(startIndex, startIndex + ENTRIES_PER_PAGE)

  const showPagination = fields.length > ENTRIES_PER_PAGE

  // Pagination handlers
  const goToPage = (page: number) => {
    const validPage = Math.max(1, Math.min(page, totalPages))
    setCurrentPage(validPage)
  }

  const navigateToInputPage = () => {
    const pageNum = Number.parseInt(pageNumberInput)
    if (!Number.isNaN(pageNum) && pageNum >= 1 && pageNum <= totalPages) {
      goToPage(pageNum)
      setPageNumberInput('')
    }
  }

  const onSubmit = (data: RampCurveSchemaType) => {
    const convertedData: AddRampCurveRequest | UpdateRampCurveRequest = {
      name: data.name,
      meteringPortId: data.meteringPortId,
      verifiedAt: data.verifiedAt,
      direction: data.direction,
      rampCurveEntries: data.rampCurveEntries.map((entry) => ({
        durationSinceStartMillis: entry.durationSinceStartMillis!,
        consumption: convertToWatts(entry.consumption)!,
      })),
    }
    onSave(convertedData)
  }

  const directionOptions = [
    { id: 'UP', value: 'UP', label: t('resources.ramp_curves.direction.up') },
    { id: 'DOWN', value: 'DOWN', label: t('resources.ramp_curves.direction.down') },
    { id: 'UNDEFINED', value: 'UNDEFINED', label: t('resources.ramp_curves.direction.undefined') },
  ]

  const addEntry = () => {
    append({
      durationSinceStartMillis: null,
      consumption: null,
    })

    // Navigate to last page if entry was added
    const newTotalPages = Math.ceil((fields.length + 1) / ENTRIES_PER_PAGE)
    setCurrentPage(newTotalPages)
  }

  const insertIntermediateEntry = (insertIndex: number) => {
    const actualInsertIndex = startIndex + insertIndex

    insert(actualInsertIndex, {
      durationSinceStartMillis: null,
      consumption: null,
    })
  }

  const removeEntry = (index: number) => {
    const actualIndex = startIndex + index
    remove(actualIndex)

    // Adjust current page if needed
    const newTotalPages = Math.ceil((fields.length - 1) / ENTRIES_PER_PAGE)
    if (currentPage > newTotalPages && newTotalPages > 0) {
      setCurrentPage(newTotalPages)
    }
  }

  return (
    <FormProvider {...methods}>
      <Card sx={{ bgcolor: 'rgba(20, 132, 160, 0.04)' }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <CustomCardContent>
            <Stack spacing={3}>
              {/* Ramp Curve Settings */}
              <Box>
                <Typography sx={{ mb: 2, fontWeight: 500, fontSize: '0.875rem' }} variant="body1">
                  {t('resources.ramp_curves.settings.title')}
                </Typography>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={2}>
                    <Box flexBasis="50%" flexGrow={1}>
                      <TextFieldController
                        required
                        error={!!errors.name}
                        label={t('resources.ramp_curves.settings.name')}
                        name="name"
                        placeholder={t('resources.ramp_curves.settings.name_placeholder')}
                        sx={{ '& .MuiInputBase-root': { backgroundColor: 'white' } }}
                      />
                      <ErrorMessage errors={errors} name="name" />
                    </Box>
                    <Box flexBasis="50%" flexGrow={1}>
                      <TextFieldController
                        error={!!errors.meteringPortId}
                        label={t('resources.ramp_curves.settings.metering_port_id')}
                        name="meteringPortId"
                        placeholder={t('resources.ramp_curves.settings.metering_port_id_placeholder')}
                        sx={{ '& .MuiInputBase-root': { backgroundColor: 'white' } }}
                      />
                      <ErrorMessage errors={errors} name="meteringPortId" />
                    </Box>
                  </Stack>
                  <Stack direction="row" spacing={2}>
                    <Box flexBasis="50%" flexGrow={1}>
                      <LocalizationProvider dateAdapter={AdapterLuxon}>
                        <Controller
                          name="verifiedAt"
                          render={({ field, fieldState: { error } }) => (
                            <DateTimePicker
                              format="dd/MM/yyyy, HH:mm"
                              label={t('resources.ramp_curves.settings.verified_at')}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  error: !!error,
                                  helperText: error?.message,
                                  InputProps: {
                                    sx: { height: '42px', backgroundColor: 'white' },
                                  },
                                },
                              }}
                              value={field.value ? DateTime.fromISO(field.value) : null}
                              onChange={(newValue) => {
                                field.onChange(newValue ? newValue.toISO() : null)
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </Box>
                    <Box flexBasis="50%" flexGrow={1}>
                      <Controller
                        name="direction"
                        render={({ field, fieldState: { error } }) => (
                          <CustomSelectField
                            fullWidth
                            required
                            error={!!error}
                            label={t('resources.ramp_curves.settings.direction')}
                            options={directionOptions}
                            sx={{
                              '& .MuiOutlinedInput-input ': {
                                backgroundColor: 'white',
                              },
                            }}
                            {...field}
                          />
                        )}
                      />
                      <ErrorMessage errors={errors} name="direction" />
                    </Box>
                  </Stack>
                </Stack>
              </Box>

              <Divider />

              {/* Ramp Curve Entries */}
              <Box>
                <Typography sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                  {t('resources.ramp_curves.entries.title')}
                </Typography>

                <ErrorMessage errors={errors} name="rampCurveEntries" />

                <Stack spacing={1}>
                  {currentPageFields.map((field, index) => (
                    <Box key={field.id}>
                      {/* Entry Content */}
                      <Box
                        sx={{
                          p: 2,
                          bgcolor: 'transparent',
                        }}
                      >
                        <Stack alignItems="center" direction="row" spacing={2}>
                          <Typography sx={{ minWidth: 60, color: 'text.secondary' }} variant="body2">
                            {t('resources.ramp_curves.entries.entry_label', { number: startIndex + index + 1 })}
                          </Typography>
                          <Box flexBasis="33%" flexGrow={1}>
                            <TextFieldController
                              error={!!errors.rampCurveEntries?.[startIndex + index]?.durationSinceStartMillis}
                              label={t('resources.ramp_curves.entries.duration_label')}
                              name={`rampCurveEntries.${startIndex + index}.durationSinceStartMillis`}
                              placeholder={t('resources.ramp_curves.entries.duration_placeholder')}
                              size="small"
                              sx={{ '& .MuiInputBase-root': { backgroundColor: 'white' } }}
                              type="number"
                              onChange={(e) => {
                                const value = e.target.value === '' ? null : Number(e.target.value)
                                methods.setValue(
                                  `rampCurveEntries.${startIndex + index}.durationSinceStartMillis`,
                                  value,
                                )
                              }}
                            />
                          </Box>
                          <Box flexBasis="33%" flexGrow={1}>
                            <TextFieldController
                              error={!!errors.rampCurveEntries?.[startIndex + index]?.consumption}
                              label={t('resources.ramp_curves.entries.consumption_label')}
                              name={`rampCurveEntries.${startIndex + index}.consumption`}
                              placeholder={t('resources.ramp_curves.entries.consumption_placeholder')}
                              size="small"
                              sx={{ '& .MuiInputBase-root': { backgroundColor: 'white' } }}
                              type="number"
                              onChange={(e) => {
                                const value = e.target.value === '' ? null : Number(e.target.value)
                                methods.setValue(`rampCurveEntries.${startIndex + index}.consumption`, value)
                              }}
                            />
                          </Box>
                          <IconButton
                            aria-label={t('resources.ramp_curves.entries.delete')}
                            color="error"
                            size="small"
                            onClick={() => removeEntry(index)}
                          >
                            <DeleteOutlined fontSize="small" />
                          </IconButton>
                          <CustomButton
                            size="small"
                            startIcon={<AddOutlined />}
                            sx={{
                              fontSize: '0.75rem',
                              borderStyle: 'dashed',
                              borderColor: 'primary.main',
                              color: 'primary.main',
                              backgroundColor: 'rgba(20, 132, 160, 0.04)',
                              minWidth: 'auto',
                              px: 1.5,
                              py: 0.5,
                              '&:hover': {
                                borderStyle: 'solid',
                                backgroundColor: 'rgba(20, 132, 160, 0.08)',
                                borderColor: 'primary.dark',
                              },
                            }}
                            variant="outlined"
                            onClick={() => insertIntermediateEntry(index)}
                          >
                            {t('resources.ramp_curves.entries.insert_before')}
                          </CustomButton>
                        </Stack>
                      </Box>
                    </Box>
                  ))}
                </Stack>

                {/* Bottom pagination controls */}
                {showPagination && (
                  <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                    <PaginationControls
                      currentPage={currentPage}
                      pageNumberInput={pageNumberInput}
                      totalPages={totalPages}
                      onPageChange={goToPage}
                      onPageNumberInputChange={setPageNumberInput}
                      onPageNumberSubmit={navigateToInputPage}
                    />
                  </Box>
                )}

                <Box sx={{ mt: 3, mb: 2 }}>
                  <CustomButton size="small" startIcon={<AddOutlined />} variant="text" onClick={addEntry}>
                    {t('resources.ramp_curves.entries.add')}
                  </CustomButton>
                </Box>
              </Box>
            </Stack>
          </CustomCardContent>
          <CustomCardActions
            deleteProps={onDelete ? { onDelete, text: t('resources.ramp_curves.delete'), withIcon: true } : undefined}
            sx={{ justifyContent: 'space-between' }}
          >
            <Stack direction="row" spacing={1}>
              <CustomButton type="submit" variant="contained">
                {t('common.button.save')}
              </CustomButton>
              <CustomButton variant="outlined" onClick={onCancel}>
                {t('common.button.cancel')}
              </CustomButton>
            </Stack>
          </CustomCardActions>
        </form>
      </Card>
    </FormProvider>
  )
}

export default RampCurveForm
